import React, { forwardRef, useCallback, useMemo } from "react";
import { useFocus } from "~/contexts/FocusContext";
import { AppCommand } from "~/types/commands";
import { ActionDefinition } from "~/types/actions";
import type { TagAction } from "~/pages/CharacterSheet/types";
import DerivedStatItem from "~/pages/CharacterSheet/widgets/DerivedStatItem/DerivedStatItem";
import StrainTracker from "~/pages/CharacterSheet/widgets/StrainTracker/StrainTracker";
import CompactItemsList from "~/pages/GmView/widgets/CompactItemsList/CompactItemsList";
import styles from "~/pages/GmView/widgets/CompactCharacterCard/CompactCharacterCard.scss";
import { CharacterData } from "~/types/storage";
import { computeHpMax, computeResilienceMax } from "~/utils/characterDerived";

export interface CompactCharacterCardProps {
  character: CharacterData;
  onUpdateHp: (hp: number) => void;
  onUpdateResilience: (resilience: number) => void;
  onUpdateStrain: (strain: number) => void;
  onApplyStrainDamage: (damage: number) => void;
  onOpenRoll: () => void;
  onMinimize: () => void;
  onOpenInNewWindow?: () => void;
  onFocusCard?: () => void;
  onUpdateItem?: (id: string, content: string) => void;
  onDeleteItem?: (id: string) => void;
  onTagAction?: (action: TagAction) => void;
  onRoll?: (name: string, pool: number, flatHits?: number) => void;
  onOpenDamageDialog: () => void;
  onOpenHealDialog: () => void;
  onOpenDirectDamageDialog: () => void;
  onOpenSpendResilienceDialog: () => void;
  isFocused?: boolean;
}

/**
 * CompactCharacterCard Component
 *
 * A compact card for displaying and managing a character in GM view.
 * Shows:
 * - Character name
 * - Core stats (read-only)
 * - HP and Resilience (editable, compact)
 * - Strain (editable, compact)
 * - Items preview (compact list)
 * - Roll and Remove buttons
 *
 * Stats have data-stat-type attributes for keyboard navigation detection.
 */
const CompactCharacterCard = forwardRef<HTMLDivElement, CompactCharacterCardProps>(({
  character,
  onUpdateHp,
  onUpdateResilience,
  onUpdateStrain,
  onApplyStrainDamage,
  onOpenRoll,
  onMinimize,
  onOpenInNewWindow,
  onFocusCard,
  onUpdateItem,
  onDeleteItem,
  onTagAction,
  onRoll,
  onOpenDamageDialog,
  onOpenHealDialog,
  onOpenDirectDamageDialog,
  onOpenSpendResilienceDialog,
  isFocused = false,
}, ref) => {
  const hpMax = computeHpMax(character.coreStats);
  const resMax = computeResilienceMax(character.coreStats);

  // Extract core stats for display
  const mind = character.coreStats.Mind ?? 0;
  const body = character.coreStats.Body ?? 0;
  const reflex = character.coreStats.Reflex ?? 0;

  const handleCommand = useCallback((command: AppCommand) => {
    switch (command.type) {
      case "open-roll-search":
        onOpenRoll();
        break;
      case "heal":
        onOpenHealDialog();
        break;
      case "damage":
        onOpenDamageDialog();
        break;
      case "direct-damage":
        onOpenDirectDamageDialog();
        break;
      case "spend-resilience":
        onOpenSpendResilienceDialog();
        break;
      case "focus-strain": {
        // Find and focus the strain tracker element
        const strainElement = (ref as React.RefObject<HTMLDivElement>)?.current?.querySelector("[data-stat-type='strain'] [tabindex='0']");
        if (strainElement instanceof HTMLElement) {
          strainElement.focus();
        }
        break;
      }
    }
  }, [onOpenRoll, onOpenHealDialog, onOpenDamageDialog, onOpenDirectDamageDialog, onOpenSpendResilienceDialog, ref]);

  const actions = useMemo<ActionDefinition[]>(() => [
    {
      id: "open-roll-search",
      label: `Roll Check`,
      description: "Search and roll skills/stats",
      keywords: ["roll", "check", "skill", "dice", character.name],
      shortcut: "R",
      category: "Rolling"
    },
    {
      id: "heal",
      label: `Heal`,
      description: "Restore HP",
      keywords: ["heal", "restore", "hp", character.name],
      shortcut: "H",
      category: "Combat"
    },
    {
      id: "damage",
      label: `Damage`,
      description: "Apply damage to HP",
      keywords: ["damage", "hurt", "hp", character.name],
      shortcut: "D",
      category: "Combat"
    },
    {
      id: "direct-damage",
      label: `Direct Damage`,
      description: "Apply damage directly to HP (bypass Resilience)",
      keywords: ["direct", "damage", "hp", character.name],
      shortcut: "X",
      category: "Combat"
    },
    {
      id: "spend-resilience",
      label: `Spend Resilience`,
      description: "Spend Resilience points",
      keywords: ["spend", "resilience", "res", character.name],
      shortcut: "V",
      category: "Combat"
    },
    {
      id: "focus-strain",
      label: `Focus Strain`,
      description: "Focus the strain tracker",
      keywords: ["focus", "strain", character.name],
      shortcut: "S",
      category: "Navigation"
    }
  ], [character.name]);

  const { activate } = useFocus(
    "CompactCharacterCard",
    {
      name: character.name,
      characterId: character.id,
    },
    handleCommand,
    actions
  );

  const handleFocus = () => {
    onFocusCard?.();
    activate();
  };

  return (
    <div
      ref={ref}
      className={`${styles.card} ${isFocused ? styles.focused : ""}`}
      tabIndex={0}
      onClick={handleFocus}
      onFocus={handleFocus}
    >
      <header className={styles.header}>
        <span className={styles.name} title={character.name}>
          {character.name}
        </span>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.rollButton}
            onClick={onOpenRoll}
            title="Open roll search (R)"
          >
            🎲
          </button>
          {onOpenInNewWindow && (
            <button
              type="button"
              className={styles.openButton}
              onClick={onOpenInNewWindow}
              title="Open in new window"
            >
              ↗
            </button>
          )}
          <button
            type="button"
            className={styles.minimizeButton}
            onClick={onMinimize}
            title="Minimize character"
          >
            −
          </button>
        </div>
      </header>

      <div className={styles.coreStats}>
        <span className={styles.stat}>
          <span className={styles.statLabel}>M</span>
          <span className={styles.statValue}>{mind}</span>
        </span>
        <span className={styles.stat}>
          <span className={styles.statLabel}>B</span>
          <span className={styles.statValue}>{body}</span>
        </span>
        <span className={styles.stat}>
          <span className={styles.statLabel}>R</span>
          <span className={styles.statValue}>{reflex}</span>
        </span>
      </div>

      <div className={styles.statsRow}>
        <div className={styles.derivedStats}>
          <div data-stat-type="hp">
            <DerivedStatItem
              label="HP"
              value={character.currentHp}
              max={hpMax}
              onChange={onUpdateHp}
              size="compact"
            />
          </div>
          <div data-stat-type="resilience">
            <DerivedStatItem
              label="Res"
              value={character.currentResilience}
              max={resMax}
              onChange={onUpdateResilience}
              size="compact"
            />
          </div>
        </div>

        <div className={styles.strainSection} data-stat-type="strain">
          <StrainTracker
            value={character.strain}
            onChange={onUpdateStrain}
            onApplyDamage={onApplyStrainDamage}
            size="compact"
          />
        </div>
      </div>

      {character.items.length > 0 && (
        <div className={styles.itemsSection}>
          <CompactItemsList
            items={character.items}
            limit={3}
            characterData={character}
            onUpdateItem={onUpdateItem}
            onDeleteItem={onDeleteItem}
            onTagAction={onTagAction}
            onRoll={onRoll}
          />
        </div>
      )}
    </div>
  );
});

CompactCharacterCard.displayName = "CompactCharacterCard";

export default CompactCharacterCard;
